/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable consistent-return */
/* eslint-disable no-undef */
import React, { ReactNode, useContext, useEffect, useRef } from 'react';
import {
  TariffExclusionsTable,
  TariffResultsTable,
  TariffFtaTable,
} from './TarrifTypes';
import { effectiveBaseDutyRate, engineFigures } from 'utils/tariffFigures';
import ReactMarkdown from 'react-markdown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { LoadingFeedback } from 'components/Common/LoadingModal';
import { ModuleContentTooltip } from 'components/Common/MUIStyledComponents';
import Switch from '@mui/material/Switch'; // Added import for the switch
import { Tooltip } from '@mui/material';
import { GlobalContext } from 'providers/GlobalProvider';
import { ImportBan, importBanResultRow } from 'utils/importBans';
import { RemissionOption } from 'utils/remissions';

interface ITariffProps {
  tariffResults: any;
  // Set while an import ban (utils/importBans) bars the line. The ban is then
  // the whole result: it stands in for the duty rows, and there is no duty,
  // fee or total to show. Saved snapshots carry it as results.activeImportBan.
  importBan?: ImportBan | null;
  productValue: number;
  materialPct?: number;
  dutyCost?: number;
  totalCost?: number;
  baseCost?: number;
  displayAdditionalRate?: string;
  // When set, the cost figures are replaced by an em dash and this message is
  // shown instead. Used where a tariff-rate quota applies but the user has
  // not declared their position: the two sides differ by orders of magnitude,
  // so rendering $0.00 would look like an answer rather than a missing input.
  costPendingMessage?: string;
  tariffCandidates?: any[];
  enabledExclusions?: Record<string, boolean>;
  toggleExclusion?: any;
  ftaOptions?: FtaOption[];
  enabledFtas?: Record<string, boolean>;
  toggleFta?: any;
  explanationText: string;
  aiLoading: boolean;
  adcvdResults?: string;
  aiExplain?: any;
  mpf?: number;
  hmf?: number;
  // NEW IEEPA PROPS
  hasIeepaTariffs?: boolean;
  hideIeepa?: boolean;
  toggleIeepa?: (val: boolean) => void;
  // Section title for the FTA toggle table. Defaults to "Free Trade
  // Agreements"; callers can override (e.g. "Preferential Treaties for
  // Mexico", "Most Favored Nations (MFN)" for Canada).
  ftaSectionTitle?: string;
  // Canada remission orders the user can claim for this transaction. An order
  // that is on removes the surtaxes it covers (see utils/remissions).
  remissionOptions?: RemissionOption[];
  enabledRemissions?: Record<string, boolean>;
  toggleRemission?: (key: string, enabled: boolean) => void;
}

type FtaOption = { code: string; name: string };

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(n || 0),
  );

const TarifFResultsComp: React.FC<ITariffProps> = ({
  tariffResults,
  productValue,
  importBan: importBanProp,
  dutyCost,
  totalCost,
  baseCost,
  displayAdditionalRate,
  costPendingMessage,
  tariffCandidates = [],
  enabledExclusions = {},
  toggleExclusion,
  ftaOptions: ftaOptionsProp,
  enabledFtas,
  toggleFta,
  explanationText,
  aiLoading,
  adcvdResults,
  aiExplain,
  mpf,
  hmf,
  hasIeepaTariffs, // Destructure new props
  hideIeepa,
  toggleIeepa,
  ftaSectionTitle = 'Free Trade Agreements',
  remissionOptions = [],
  enabledRemissions = {},
  toggleRemission,
}) => {
  const { isWhiteLabel } = useContext(GlobalContext);
  const componentRef = useRef<HTMLDivElement>(null);
  const d10 = (s: string) => String(s || '').replace(/\D/g, '');
  const safeTariffResults =
    tariffResults && typeof tariffResults === 'object' ? tariffResults : {};

  if ((window as any).DEBUG_CODE) {
    const WANT = d10(String((window as any).DEBUG_CODE));
    const debugLines = Array.isArray(safeTariffResults.results?.lines)
      ? safeTariffResults.results.lines
      : Array.isArray(safeTariffResults.lines)
      ? safeTariffResults.lines
      : [];
    const debugResults = Array.isArray(safeTariffResults.results?.summaryRows)
      ? safeTariffResults.results.summaryRows
      : Array.isArray(safeTariffResults.results)
      ? safeTariffResults.results
      : [];
    // eslint-disable-next-line
    console.log('[TarifFResultsComp DEBUG]', WANT, {
      lines: debugLines.map((ln: any) => ({
        id: ln.id,
        label: ln.label,
        value: ln.value,
        codes: (ln.rows || [])
          .map((r: any) => d10(r._codeDigits || r.code || r.type))
          .filter(Boolean),
      })),
      results: debugResults.map((r: any) => ({
        type: r.type,
        code: r.code,
        digits: d10(r._codeDigits || r.code || r.type),
      })),
    });
  }

  // --- Normalize "live" vs "snapshot" shapes --------------------------------
  const isSnapshot = Boolean(
    safeTariffResults.inputs &&
      safeTariffResults.base &&
      safeTariffResults.results,
  );

  // An explicit null means the gate answers lifted the ban, so only an omitted
  // prop falls back to the saved snapshot.
  const importBan: ImportBan | null =
    importBanProp !== undefined
      ? importBanProp
      : isSnapshot
      ? safeTariffResults.results?.activeImportBan ?? null
      : null;
  const figuresWithheld = Boolean(costPendingMessage || importBan);

  const savedExplanation: string = isSnapshot
    ? String(safeTariffResults.explanation?.text || '')
    : '';

  // per-line data
  const linesRaw: any[] = isSnapshot
    ? Array.isArray(safeTariffResults.results?.lines)
      ? safeTariffResults.results.lines
      : []
    : Array.isArray(safeTariffResults.lines)
    ? safeTariffResults.lines
    : [];

  const explanationToShow = isSnapshot
    ? savedExplanation
    : explanationText || '';
  const aiBusy = isSnapshot ? false : aiLoading;

  const lines = Array.isArray(linesRaw) ? linesRaw : [];
  const hasSplit = lines.length > 0;

  // rows shown in "Duty Summary"
  const summaryRows: any[] = isSnapshot
    ? Array.isArray(safeTariffResults.results?.summaryRows)
      ? safeTariffResults.results.summaryRows
      : []
    : Array.isArray(safeTariffResults.results)
    ? safeTariffResults.results
    : [];

  // base duty rate and description
  const baseDutyRate: string =
    (isSnapshot
      ? safeTariffResults.base?.baseDutyRate
      : safeTariffResults.baseDutyRate) ??
    summaryRows.find(
      (r: any) => r?.type === 'Base Duty' || r?.type === 'Base Duty (Column 2)',
    )?.rate ??
    '—';

  // The engine's figures: live results carry them as engineMath, a saved
  // snapshot as math.
  const engineMath = isSnapshot
    ? safeTariffResults.math
    : safeTariffResults.engineMath;
  const baseDutyRateDisplay = effectiveBaseDutyRate(
    baseDutyRate,
    lines,
    engineMath,
    isSnapshot
      ? Number(safeTariffResults.inputs?.productValue || 0)
      : productValue,
  );

  const productDesc: string =
    (isSnapshot
      ? safeTariffResults.base?.productDesc
      : safeTariffResults.productDesc) ?? '';

  const additionalRateDisplay: string =
    displayAdditionalRate ??
    (isSnapshot
      ? safeTariffResults.math?.additionalDutyRateDisplay
      : safeTariffResults.additionalDutyRate) ??
    '—';

  const mpfToShow: number | undefined =
    typeof mpf === 'number'
      ? mpf
      : isSnapshot
      ? Number(safeTariffResults.math?.mpf ?? NaN)
      : undefined;

  const hmfToShow: number | undefined =
    typeof hmf === 'number'
      ? hmf
      : isSnapshot
      ? Number(safeTariffResults.math?.hmf ?? NaN)
      : undefined;

  const ftaOptions: FtaOption[] =
    ftaOptionsProp ??
    (isSnapshot
      ? (safeTariffResults.results?.ftaOptions as FtaOption[]) ?? []
      : []);

  const candidatesForExclusions: any[] = tariffCandidates.length
    ? tariffCandidates
    : isSnapshot
    ? safeTariffResults.results?.optionalExclusions ?? []
    : safeTariffResults.optionalExclusions ?? [];

  // A switch reads ON when the user switched it on OR the engine applied the
  // exclusion by itself, so the list agrees with the line breakdown. A switch
  // the user turned off stays off; the engine never auto-applies a declined code.
  const appliedCodes = new Set(
    [
      ...summaryRows,
      ...lines.flatMap((l: any) => (Array.isArray(l?.rows) ? l.rows : [])),
    ]
      .map((r: any) => d10(r?._codeDigits || r?.code || ''))
      .filter(Boolean),
  );
  const exclusionSwitches: Record<string, boolean> = {
    ...(enabledExclusions || {}),
  };
  candidatesForExclusions.forEach((c: any) => {
    const code = c?.codeDigits;
    if (
      code &&
      appliedCodes.has(d10(code)) &&
      exclusionSwitches[code] !== false
    ) {
      exclusionSwitches[code] = true;
    }
  });

  const dutyCostDisplay =
    dutyCost ??
    (isSnapshot ? Number(safeTariffResults.math?.dutyCost || 0) : 0);
  const totalCostDisplay =
    totalCost ??
    (isSnapshot ? Number(safeTariffResults.math?.totalCost || 0) : 0);

  const [dots, setDots] = React.useState('');

  useEffect(() => {
    if (!aiLoading) {
      setDots('');
      return;
    }
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : `${d}.`));
    }, 350);
    return () => clearInterval(id);
  }, [aiLoading]);

  // A barred line has nothing to explain beyond the ban shown above it.
  const shouldShowAiSection =
    !importBan &&
    (explanationToShow.length > 0 ||
      (!isSnapshot && (Boolean(aiExplain) || aiBusy)));

  // Cost figures read as answers, so while a declaration is outstanding, or
  // when a ban leaves nothing to price, they must not render as $0.00.
  const money = (n: number) => (figuresWithheld ? '—' : usd(n));

  const baseCostDisplay =
    typeof baseCost === 'number'
      ? baseCost
      : isSnapshot
      ? engineFigures(engineMath).baseDuty
      : 0;

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {/* LEFT */}
        <div className='tariff-container-left'>
          <div>
            <div
              className='screening-ai-label-text'
              style={{ marginBottom: '5px' }}
            >
              Product Description
            </div>
            <div className='tariff-desc-text'>{productDesc}</div>
          </div>

          {importBan ? (
            <div style={{ width: '100%' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginBottom: '5px' }}
              >
                Duty Summary
              </div>
              <div
                className='screening-badge chip-red lens-chip'
                style={{
                  display: 'block',
                  width: '100%',
                  margin: '0 0 12px 0',
                  padding: '10px 14px',
                  lineHeight: 1.45,
                }}
              >
                {importBan.narrative}. These goods can&apos;t be entered, so no
                duty applies.
              </div>
              <TariffResultsTable
                tariffResults={[importBanResultRow(importBan)]}
              />
              {importBan.description ? (
                <div className='tariff-desc-text' style={{ marginTop: '8px' }}>
                  {importBan.description}
                </div>
              ) : null}
            </div>
          ) : !hasSplit ? (
            <div style={{ width: '100%' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginBottom: '5px' }}
              >
                Duty Summary
              </div>
              <TariffResultsTable tariffResults={summaryRows} />
            </div>
          ) : (
            <div style={{ width: '100%' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginBottom: '5px' }}
              >
                Duty Summary • Line Breakdown
              </div>

              {lines.map((line: any, idx: number) => {
                const lineValueDisplay = Number(line?.value ?? 0);
                // The engine's own per-line duty. A ticket saved before the
                // engine reported one has none to show.
                const lineDuty =
                  typeof line?.duty === 'number' ? usd(line.duty) : '—';

                return (
                  <div
                    key={line?.id || idx}
                    style={{ marginTop: idx === 0 ? 12 : 18 }}
                  >
                    <div className='screening-ai-label-text'>
                      {line?.label || `Line ${idx + 1}`}
                      <span style={{ float: 'right' }}>
                        Value: <strong>{usd(lineValueDisplay)}</strong> • Duty:{' '}
                        <strong>{lineDuty}</strong>
                      </span>
                    </div>
                    <TariffResultsTable tariffResults={line?.rows} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Free Trade Agreements (label overridable per country). Neither
              these nor the exclusions below apply to a barred line. */}
          {!importBan && Array.isArray(ftaOptions) && ftaOptions.length > 0 && (
            <div style={{ width: '100%', marginTop: '18px' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginBottom: '5px' }}
              >
                {ftaSectionTitle}
              </div>
              <TariffFtaTable
                options={ftaOptions}
                enabled={enabledFtas}
                onToggle={toggleFta}
              />
            </div>
          )}

          {!importBan && remissionOptions.length > 0 && (
            <div style={{ width: '100%', marginTop: '18px' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginBottom: '5px' }}
              >
                Remission Orders
              </div>
              <div className='tariff-desc-text' style={{ marginBottom: '5px' }}>
                Turn an order on only if this transaction meets its conditions
                (see Relief &amp; Exemptions). It removes the surtax the order
                covers from the duty.
              </div>
              <TariffFtaTable
                options={remissionOptions}
                enabled={enabledRemissions}
                onToggle={toggleRemission}
              />
            </div>
          )}

          {/* Optional Exclusions */}
          {!importBan && (
            <div style={{ width: '100%' }}>
              {/* NEW IEEPA TOGGLE */}
              {hasIeepaTariffs && (
                <div
                  style={{
                    width: '100%',
                    marginBottom: '18px',
                    marginTop: '18px',
                  }}
                >
                  <div
                    className='screening-ai-label-text'
                    style={{ marginBottom: '5px' }}
                  >
                    Would you like to view results without IEEPA Tariffs?
                  </div>
                  <table className='tariff-table'>
                    <tbody>
                      <tr>
                        <td className='tariff-col-type'>
                          Remove IEEPA Tariffs
                        </td>
                        <td
                          style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                        >
                          <span
                            style={{
                              marginRight: '8px',
                              fontSize: '14px',
                              verticalAlign: 'middle',
                              color: 'var(--text-primary)',
                            }}
                          >
                            {hideIeepa ? 'Yes' : 'No'}
                          </span>
                          <Switch
                            size='small'
                            checked={Boolean(hideIeepa)}
                            onChange={(e) => toggleIeepa?.(e.target.checked)}
                            disabled={!toggleIeepa}
                            inputProps={{
                              'aria-label': 'Toggle IEEPA Tariffs',
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div
                className='screening-ai-label-text'
                style={{
                  marginBottom: '5px',
                  marginTop: hasIeepaTariffs ? '0px' : '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                Potential Tariffs & Exclusions
                <Tooltip
                  title={
                    <span>
                      For details on HTS Exclusions visit the{' '}
                      <a
                        href='https://bitedata.notion.site/bite-tariff-exception'
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{
                          color: '#007BFF',
                          textDecoration: 'underline',
                        }}
                      >
                        BITE Informational Page
                      </a>
                      .
                    </span>
                  }
                  placement='right'
                  arrow
                  interactive
                  componentsProps={{
                    tooltip: {
                      sx: {
                        display: 'flex',
                        padding:
                          'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        alignSelf: 'stretch',
                        borderRadius: 'var(--radius-md, 8px)',
                        background: 'var(--Background-200, var(--bg-colors))',
                        color: 'var(--text-prim25)',
                        maxWidth: '700px',
                        '& .MuiTooltip-arrow': {
                          color: 'var(--Background-200, var(--bg-colors))',
                        },
                      },
                    },
                  }}
                >
                  <div className='module-header-help'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                      fill='none'
                    >
                      <g clipPath='url(#clip0_16034_866)'>
                        <path
                          d='M7.57484 7.50001C7.77076 6.94306 8.15746 6.47343 8.66647 6.17428C9.17547 5.87514 9.77392 5.76579 10.3558 5.8656C10.9377 5.96541 11.4655 6.26794 11.8457 6.71961C12.226 7.17128 12.4341 7.74294 12.4332 8.33334C12.4332 10 9.93317 10.8333 9.93317 10.8333M9.99984 14.1667H10.0082M18.3332 10C18.3332 14.6024 14.6022 18.3333 9.99984 18.3333C5.39746 18.3333 1.6665 14.6024 1.6665 10C1.6665 5.39763 5.39746 1.66667 9.99984 1.66667C14.6022 1.66667 18.3332 5.39763 18.3332 10Z'
                          stroke='#94969C'
                          strokeWidth='1.66667'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </g>
                      <defs>
                        <clipPath id='clip0_16034_866'>
                          <rect width='20' height='20' fill='white' />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                </Tooltip>
              </div>
              <TariffExclusionsTable
                candidates={candidatesForExclusions}
                enabled={exclusionSwitches}
                onToggle={(code: any, enabled: any) =>
                  toggleExclusion?.(code, enabled)
                }
              />
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div
          style={{
            display: 'flex',
            minWidth: '300px',
            maxWidth: '40%',
            flexDirection: 'column',
          }}
        >
          <div className='tariff-container-right '>
            <div className='screening-ai-label-text'>Cost Summary</div>

            {figuresWithheld ? (
              <div className='tariff-cost-pending'>
                {importBan
                  ? 'Excluded from importation, so no duty or fees apply.'
                  : costPendingMessage}
              </div>
            ) : null}

            <div className='screening-ai-row'>
              <div className='tariff-desc-text'>Base Duty Rate:</div>
              <div className='tariff-desc-text'>
                {figuresWithheld ? '—' : baseDutyRateDisplay}
              </div>
            </div>

            <div className='screening-ai-row'>
              <div className='tariff-desc-text'>Base Duty Cost:</div>

              <div className='tariff-desc-text'>{money(baseCostDisplay)}</div>
            </div>

            <div className='screening-ai-row'>
              <div className='tariff-desc-text'>Additional Duty Rate:</div>
              <div className='tariff-desc-text'>
                {figuresWithheld ? '—' : additionalRateDisplay}
              </div>
            </div>

            <div className='screening-ai-row'>
              <div className='tariff-desc-text'>Additional Duty Cost:</div>
              <div className='tariff-desc-text'>
                {money(dutyCostDisplay - baseCostDisplay)}
              </div>
            </div>

            <div className='screening-ai-row'>
              <div className='screening-ai-label-text'>Total Duty Cost:</div>
              <div className='screening-ai-label-text'>
                {money(dutyCostDisplay)}
              </div>
            </div>

            {!importBan && typeof mpfToShow === 'number' && mpfToShow > 0 && (
              <div className='screening-ai-row'>
                <div className='tariff-desc-text'>
                  <ModuleContentTooltip
                    title={
                      <p className='module-header-tooltip-text'>
                        Merchandise Processing Fee
                      </p>
                    }
                    placement='top'
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          display: 'flex',
                          padding:
                            'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          alignSelf: 'stretch',
                          borderRadius: 'var(--radius-md, 8px)',
                          background: 'var(--Background-200, var(--bg-colors))',
                          color: 'var(--text-prim25)',
                          maxWidth: '400px',
                          '& .MuiTooltip-arrow': {
                            color: 'var(--Background-200, var(--bg-colors))',
                          },
                        },
                      },
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                    >
                      <path
                        d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                        fill='var(--text-secondary)'
                      />
                    </svg>
                  </ModuleContentTooltip>{' '}
                  MPF:
                </div>
                <div className='tariff-desc-text'>{usd(mpfToShow)}</div>
              </div>
            )}

            {!importBan && typeof hmfToShow === 'number' && hmfToShow > 0 && (
              <div className='screening-ai-row'>
                <div className='tariff-desc-text'>
                  {' '}
                  <ModuleContentTooltip
                    title={
                      <p className='module-header-tooltip-text'>
                        Harbor Maintenance Fee
                      </p>
                    }
                    placement='top'
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          display: 'flex',
                          padding:
                            'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          alignSelf: 'stretch',
                          borderRadius: 'var(--radius-md, 8px)',
                          background: 'var(--Background-200, var(--bg-colors))',
                          color: 'var(--text-prim25)',
                          maxWidth: '400px',
                          '& .MuiTooltip-arrow': {
                            color: 'var(--Background-200, var(--bg-colors))',
                          },
                        },
                      },
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                    >
                      <path
                        d='M9 8.25C8.80109 8.25 8.61033 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V12C8.25 12.1989 8.32902 12.3897 8.46967 12.5303C8.61033 12.671 8.80109 12.75 9 12.75C9.19892 12.75 9.38968 12.671 9.53033 12.5303C9.67099 12.3897 9.75 12.1989 9.75 12V9C9.75 8.80109 9.67099 8.61032 9.53033 8.46967C9.38968 8.32902 9.19892 8.25 9 8.25ZM9.285 5.31C9.10241 5.23499 8.8976 5.23499 8.715 5.31C8.62294 5.3457 8.53883 5.39922 8.4675 5.4675C8.40126 5.5404 8.34799 5.62411 8.31 5.715C8.26802 5.80401 8.24747 5.90162 8.25 6C8.24943 6.0987 8.26835 6.19655 8.30568 6.28793C8.343 6.37931 8.39799 6.46242 8.4675 6.5325C8.5404 6.59875 8.62412 6.65202 8.715 6.69C8.82863 6.73668 8.95198 6.75474 9.07421 6.74258C9.19645 6.73043 9.31383 6.68844 9.41604 6.6203C9.51825 6.55216 9.60215 6.45996 9.66039 6.3518C9.71862 6.24364 9.74939 6.12284 9.75 6C9.74724 5.80142 9.66955 5.61123 9.5325 5.4675C9.46118 5.39922 9.37707 5.3457 9.285 5.31ZM9 1.5C7.51664 1.5 6.0666 1.93987 4.83323 2.76398C3.59986 3.58809 2.63856 4.75943 2.07091 6.12987C1.50325 7.50032 1.35472 9.00832 1.64411 10.4632C1.9335 11.918 2.64781 13.2544 3.6967 14.3033C4.7456 15.3522 6.08197 16.0665 7.53683 16.3559C8.99168 16.6453 10.4997 16.4968 11.8701 15.9291C13.2406 15.3614 14.4119 14.4001 15.236 13.1668C16.0601 11.9334 16.5 10.4834 16.5 9C16.5 8.01509 16.306 7.03982 15.9291 6.12987C15.5522 5.21993 14.9997 4.39314 14.3033 3.6967C13.6069 3.00026 12.7801 2.44781 11.8701 2.0709C10.9602 1.69399 9.98492 1.5 9 1.5ZM9 15C7.81332 15 6.65328 14.6481 5.66658 13.9888C4.67989 13.3295 3.91085 12.3925 3.45673 11.2961C3.0026 10.1997 2.88378 8.99334 3.11529 7.82946C3.3468 6.66557 3.91825 5.59647 4.75736 4.75736C5.59648 3.91824 6.66558 3.3468 7.82946 3.11529C8.99335 2.88378 10.1997 3.0026 11.2961 3.45672C12.3925 3.91085 13.3295 4.67988 13.9888 5.66658C14.6481 6.65327 15 7.81331 15 9C15 10.5913 14.3679 12.1174 13.2426 13.2426C12.1174 14.3679 10.5913 15 9 15Z'
                        fill='var(--text-secondary)'
                      />
                    </svg>
                  </ModuleContentTooltip>{' '}
                  HMF:
                </div>
                <div className='tariff-desc-text'>{usd(hmfToShow)}</div>
              </div>
            )}

            {!importBan &&
              ((Number(mpfToShow) || 0) > 0 ||
                (Number(hmfToShow) || 0) > 0) && (
                <div className='screening-ai-row'>
                  <div className='screening-ai-label-text'>
                    Duties and Fees:
                  </div>
                  <div className='screening-ai-label-text'>
                    {usd(
                      (Number(dutyCostDisplay) || 0) +
                        (Number(hmfToShow) || 0) +
                        (Number(mpfToShow) || 0),
                    )}
                  </div>
                </div>
              )}

            <div className='tariff-results-line-div'></div>

            <div className='screening-ai-row' style={{ marginTop: 'auto' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginRight: '10px' }}
              >
                Total Cost:
              </div>
              <div className='tariff-total-cost'>
                {figuresWithheld ? '—' : usd(totalCostDisplay)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {shouldShowAiSection && (
        <div className='screening-ai-container' style={{ marginTop: '15px' }}>
          <section className='ai-card ai-card--osint' style={{ width: '100%' }}>
            <div
              className='screening-ai-label-text'
              style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
            >
              <AutoAwesomeIcon sx={{ color: '#4895ef' }} />
              {isWhiteLabel ? 'AI Explanation:' : `BITE's AI Explanation:`}
            </div>
            <p
              className='screening-ai-sum-result-text'
              style={{ margin: '2px 0 8px', opacity: 0.85 }}
            >
              Press &apos;Explain Results&apos; below to receive a summary
              explanation for each duty and/or tariff line item
            </p>

            {/* Controls */}
            <div className='screening-input-submit-group'>
              <button
                className='btn-new-automate'
                onClick={aiExplain}
                disabled={aiLoading}
              >
                <div>
                  {explanationToShow.length
                    ? 'Re-run Explanation'
                    : 'Explain Results'}
                </div>
              </button>
            </div>

            {/* Loading / Error */}
            {aiLoading && (
              <div style={{ marginTop: 6 }}>
                <LoadingFeedback size='small' />
              </div>
            )}

            {/* Results */}
            {!aiLoading && explanationToShow.length > 0 && (
              <div
                style={{ display: 'flex', flex: 1, flexDirection: 'column' }}
              >
                <div className='screening-ai-sum-groups'>
                  <div
                    className='screening-ai-sum-header-texts'
                    data-usetiful={
                      !aiBusy && explanationToShow.length > 0
                        ? 'tariff-results-done'
                        : ''
                    }
                  >
                    <ReactMarkdown
                      components={{
                        h1: ({ children }: { children: ReactNode }) => (
                          <h1
                            style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                          >
                            {children}
                          </h1>
                        ),
                        h2: ({ children }: { children: ReactNode }) => (
                          <h2
                            style={{ fontSize: '1.3rem', fontWeight: 'bold' }}
                          >
                            {children}
                          </h2>
                        ),
                        h3: ({ children }: { children: ReactNode }) => (
                          <h3
                            style={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                          >
                            {children}
                          </h3>
                        ),
                        p: ({ children }: { children: ReactNode }) => (
                          <p style={{ marginBottom: '0.75rem' }}>{children}</p>
                        ),
                        ul: ({ children }: { children: ReactNode }) => (
                          <ul
                            style={{
                              paddingLeft: '1.2rem',
                              marginBottom: '0.75rem',
                            }}
                          >
                            {children}
                          </ul>
                        ),
                        li: ({ children }: { children: ReactNode }) => (
                          <li style={{ marginBottom: '0.3rem' }}>{children}</li>
                        ),
                        strong: ({ children }: { children: ReactNode }) => (
                          <strong style={{ fontWeight: 600 }}>
                            {children}
                          </strong>
                        ),
                      }}
                    >
                      {aiBusy
                        ? `Generating explanation${dots}`
                        : explanationToShow}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
};

export default TarifFResultsComp;
